import { isScrolledToTop, smoothScroll, getScrollPosition } from '@/utils/dom'
import { Link, usePage } from '@inertiajs/react'
import { getLocale, localizedPath } from '@/utils/locale'
import Routes from '@/constants/routes.tsx'
import { parseURL, decodeHash } from '@/utils/url'

type LinkComponentProps = {
  children: any
  to?: string
  scrollToOffset?: number
  [key: string]: any
}

export default function LinkComponent({
  children,
  to,
  scrollToOffset = undefined,
  onClick,
  ...other
}: LinkComponentProps) {
  if (!to) return <span {...other}>{children}</span>

  const { url } = usePage()
  let toPath = localizedPath(to, getLocale(url))

  // hash links to the current page but happen to include the whole path should have that axed
  // /download/#cli => #cli
  const { pathname: currentPath } = parseURL(url)
  const { origin, pathname, hash } = parseURL(toPath)
  if (origin === Routes.hostname && currentPath === pathname && hash && !toPath.startsWith('#')) {
    toPath = toPath.substring(pathname.length)
  }

  // internal hash links
  if (toPath.startsWith('#')) {
    return (
      <a
        href={toPath}
        onClick={(e) => {
          if (typeof onClick === 'function') onClick(e)
          e.preventDefault()
          const element = document.getElementById(decodeHash(hash.substring(1)))
          const documentScrollPosition = { top: 0, left: 0 }
          if (element) {
            if (element && getComputedStyle(element)?.display === 'none') {
              element.style.display = 'block'
            }
            smoothScroll(e, scrollToOffset, false)
            documentScrollPosition.top = getScrollPosition(element)
          }
          history.pushState({ documentScrollPosition }, '', toPath)
        }}
        {...other}
      >
        {children}
      </a>
    )
  }

  // download links
  if (toPath.startsWith(`${Routes.hostname}/download/?app=`)) {
    return (
      <a href={toPath} download {...other}>
        {children}
      </a>
    )
  }

  // internal links
  if (toPath.startsWith('/') && !toPath.startsWith('//') && !toPath.includes('.')) {
    // when on same page, scroll to the top
    // or if already at the top, reload page
    if (currentPath === pathname) {
      return (
        <a
          href={toPath}
          onClick={(e) => {
            if (typeof onClick === 'function') onClick(e)
            if (!isScrolledToTop()) {
              e.preventDefault()
              window.scrollTo({ top: 0, behavior: 'smooth' })
              window.history.replaceState({ documentScrollPosition: { top: 0, left: 0 } }, '', toPath)
            }
          }}
          {...other}
        >
          {children}
        </a>
      )
    }

    return (
      <Link href={toPath} prefetch preserveScroll={true} onClick={onClick} {...other}>
        {children}
      </Link>
    )
  }

  // everything else asides from vault login is an external link or resource, open in a new tab
  const isVaultLink = toPath === Routes.login || toPath === Routes.loginEu
  return (
    <a href={toPath} target={isVaultLink ? '_self' : '_blank'} rel="noopener noreferrer" {...other}>
      {children}
    </a>
  )
}
